import React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import { AttachmentFields } from "../helper/volunteer.constant";

export interface ExistingAttachment {
  id?: number;
  attachmentid?: number;
  filename?: string;
  name?: string;
  fileextension?: string;
  extension?: string;
  size?: string | number;
  file?: File;
  attachmentType?: string;
  attachmenttype?: string;
  typeLabel?: string;
  attachmenttypetext?: string;
  path?: string;
}

export interface StagedFile {
  id: string;
  filename: string;
  fileextension: string | undefined;
  size: string;
  file: File;
  attachmentType: string;
  typeLabel: string;
}

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onSave: (fileData: any[]) => void;
  options: { value: string | number; label: string }[];
  existingAttachments?: ExistingAttachment[];
}

interface AttachmentFormValues {
  attachmentType: string;
  files: any;
}

const getAttachmentSizeBytes = (attachment: ExistingAttachment): number => {
  if (!attachment) return 0;
  if (attachment.file instanceof File) {
    return attachment.file.size;
  }
  if (typeof attachment.size === "number") {
    return attachment.size;
  }
  if (typeof attachment.size === "string") {
    const sizeStr = attachment.size.trim().toLowerCase();
    const value = parseFloat(sizeStr);
    if (isNaN(value)) return 0;
    if (sizeStr.endsWith("mb")) {
      return value * 1024 * 1024;
    }
    if (sizeStr.endsWith("kb")) {
      return value * 1024;
    }
    if (sizeStr.endsWith("gb")) {
      return value * 1024 * 1024 * 1024;
    }
    return value;
  }
  return 0;
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const validateFiles = (
  filesToValidate: File[],
  existingAttachments: ExistingAttachment[],
  stagedFiles: StagedFile[]
): { isValid: boolean; error: string | null } => {
  // 1. Validate PDF MIME type and extension
  const nonPdf = filesToValidate.some(
    (file) =>
      file.type !== "application/pdf" ||
      !file.name.toLowerCase().endsWith(".pdf")
  );
  if (nonPdf) {
    return { isValid: false, error: "Only PDF files are allowed." };
  }

  // 2. Prevent duplicate files (check name and size)
  for (const file of filesToValidate) {
    const isDuplicateInStaged = stagedFiles.some(
      (sf) => sf.filename === file.name && sf.file.size === file.size
    );
    const isDuplicateInExisting = existingAttachments.some((ea) => {
      const eaName = ea.filename || ea.name;
      const eaSize = getAttachmentSizeBytes(ea);
      return eaName === file.name && eaSize === file.size;
    });

    if (isDuplicateInStaged || isDuplicateInExisting) {
      return {
        isValid: false,
        error: `File "${file.name}" has already been selected or uploaded.`,
      };
    }
  }

  // 3. Validate total size restriction (20 MB)
  const maxSizeBytes = 20 * 1024 * 1024;
  const existingSize = existingAttachments.reduce(
    (sum, att) => sum + getAttachmentSizeBytes(att),
    0
  );
  const stagedSize = stagedFiles.reduce(
    (sum, sf) => sum + (sf.file?.size || 0),
    0
  );
  const newFilesSize = filesToValidate.reduce(
    (sum, file) => sum + file.size,
    0
  );

  if (existingSize + stagedSize + newFilesSize > maxSizeBytes) {
    const remainingBytes = Math.max(0, maxSizeBytes - existingSize - stagedSize);
    return {
      isValid: false,
      error: `Total attachment size cannot exceed 20 MB. Remaining allowed size: ${formatFileSize(remainingBytes)}.`,
    };
  }

  return { isValid: true, error: null };
};

const AttachmentUploadModal: React.FC<Props> = ({
  isOpen,
  toggle,
  onSave,
  options,
  existingAttachments = [],
}) => {

  const {
    control,
    handleSubmit,
    reset,
    resetField,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AttachmentFormValues>({
    defaultValues: {
      attachmentType: "",
      files: null,
    },
  });

  const [fileInputKey, setFileInputKey] = useState<number>(0);

  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  const typeValue = watch("attachmentType");
  const fileValue = watch("files");

  const stagedFilesRef = useRef(stagedFiles);
  useEffect(() => {
    stagedFilesRef.current = stagedFiles;
  }, [stagedFiles]);

  useEffect(() => {
    if (typeValue && fileValue) {
      setValidationError(null);

      const typeLabel =
        options.find((o) => String(o.value) === String(typeValue))?.label || typeValue;

      const fileArray = Array.isArray(fileValue)
        ? fileValue
        : [fileValue].filter((f) => f);

      if (fileArray.length > 0) {
        const validation = validateFiles(fileArray, existingAttachments, stagedFilesRef.current);
        if (!validation.isValid) {
          setValidationError(validation.error);
          resetField("files");
          setFileInputKey((k) => k + 1);
          return;
        }

        const newStaged = fileArray.map((file: File) => ({
          id: `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
          filename: file.name,
          fileextension: file.name.split(".").pop(),
          size: formatFileSize(file.size),
          file,
          attachmentType: typeValue,
          typeLabel: typeLabel,
        }));

        setStagedFiles((prev) => [...prev, ...newStaged]);

        resetField("attachmentType");
        resetField("files");
        setFileInputKey((k) => k + 1);
      }
    }
  }, [typeValue, fileValue, options, resetField, existingAttachments]);

  useEffect(() => {
    if (isOpen) {
      reset({ attachmentType: "", files: null });
      setPreviewFile(null);
      setPreviewOpen(false);
      setStagedFiles([]);
      setValidationError(null);
      setFileInputKey((k) => k + 1);
    }
  }, [isOpen, reset]);

  // Filter options based on what's already staged strictly by value
  const availableOptions = options.filter((opt) => {
    const optValueStr = String(opt.value).toLowerCase();
    return !stagedFiles.some(
      (sf) => String(sf.attachmentType).toLowerCase() === optValueStr
    );
  });

  /* -------------------------
   PREVIEW HANDLER
------------------------- */
  const handlePreview = (file: File) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  /* -------------------------
     SAVE HANDLER
  ------------------------- */


  const onSubmit = () => {
    if (stagedFiles.length === 0) return;

    onSave(stagedFiles);

    reset({ attachmentType: "", files: null });
    setStagedFiles([]);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} backdrop="static">
      <ModalHeader toggle={toggle}>
        Upload Attachment
      </ModalHeader>

      <ModalBody>
        {validationError && (
          <div className="alert alert-danger mt-2">
            {validationError}
          </div>
        )}
        {availableOptions.length > 0 ? (
          <CommonForm
            key={fileInputKey}
            fields={AttachmentFields(availableOptions, stagedFiles)}
            control={control}
            errors={errors}
            columns={1}
            setValue={setValue}
            openPreview={handlePreview}
          />
        ) : (
          <div className="alert alert-success mt-2 mb-0">
            Maximum allowed attachments reached.
          </div>
        )}

        {stagedFiles.length > 0 && (
          <div className="mt-3">
            <h6 className="fw-bold mb-2 small text-muted text-uppercase">
              Selected Attachments
            </h6>
            {stagedFiles.map((sf) => (
              <div
                key={sf.id}
                className="d-flex justify-content-between align-items-center p-2 mb-2 border rounded bg-light"
              >
                <div>
                  <div className="fw-semibold text-primary">
                    {sf.typeLabel.toUpperCase()}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted text-truncate" style={{ maxWidth: '200px' }}>
                      {sf.filename}
                    </span>
                    <span className="badge bg-secondary">{sf.size}</span>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <i
                    className="ri-eye-line text-primary"
                    title="Preview"
                    role="button"
                    tabIndex={0}
                    aria-label={`Preview ${sf.filename}`}
                    onClick={() => handlePreview(sf.file)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handlePreview(sf.file);
                      }
                    }}
                    style={{ fontSize: "16px", cursor: "pointer" }}
                  />
                  <i
                    className="ri-delete-bin-line text-danger"
                    title="Remove"
                    role="button"
                    tabIndex={0}
                    aria-label={`Remove ${sf.filename}`}
                    onClick={() => {
                      setValidationError(null);
                      setStagedFiles((prev) =>
                        prev.filter((item) => item.id !== sf.id)
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setValidationError(null);
                        setStagedFiles((prev) =>
                          prev.filter((item) => item.id !== sf.id)
                        );
                      }
                    }}
                    style={{ fontSize: "16px", cursor: "pointer" }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </ModalBody>

      <ModalFooter>

        <Button outline onClick={toggle}>
          Cancel
        </Button>

        <Button color="primary" onClick={handleSubmit(onSubmit)}>
          Upload
        </Button>

      </ModalFooter>
      <FilePreviewModal
        file={previewFile}
        fileUrl={null}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
    </Modal>


  );
};

export default AttachmentUploadModal;